import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ArticleCard from '../components/ArticleCard';

function BlogList() {
  const [searchParams, setSearchParams] = useSearchParams();

  // State management
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('busca') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('categoria') || '');
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page') || '1'));
  const [totalPages, setTotalPages] = useState(1);
  const [mostViewedArticles, setMostViewedArticles] = useState([]);

  // Fetch articles
  const fetchArticles = async (page = 1, search = '', category = '') => {
    setLoading(true);
    try {
      const { data } = await axios.get('/api/blog/artigos', {
        params: {
          page,
          limit: 12,
          busca: search,
          categoria: category,
        },
      });

      setArticles(data.artigos || []);
      setTotalPages(data.totalPages || 1);
    } catch (error) {
      console.error('Error fetching articles:', error);
      setArticles([]);
    } finally {
      setLoading(false);
    }
  };

  // Fix category accents
  const fixCategoryAccent = (nome) => {
    const accentMap = {
      'Legislacao': 'Legislação',
      'Tendencias': 'Tendências',
      'Dicas de Construcao': 'Dicas de Construção',
      'Construcao': 'Construção',
      'Orcamento': 'Orçamento',
      'Tecnologia': 'Tecnologia',
      'Sustentabilidade': 'Sustentabilidade',
    };
    return accentMap[nome] || nome;
  };

  // Fetch categories
  const fetchCategories = async () => {
    try {
      const { data } = await axios.get('/api/blog/categorias');
      const raw = Array.isArray(data) ? data : data.categorias || [];
      setCategories(raw.map(cat => ({ ...cat, nome: fixCategoryAccent(cat.nome) })));
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]);
    }
  };

  // Fetch most viewed articles
  const fetchMostViewed = async () => {
    try {
      const { data } = await axios.get('/api/blog/artigos', {
        params: {
          limit: 5,
          destaque: true,
        },
      });
      setMostViewedArticles((data.artigos || []).slice(0, 5));
    } catch (error) {
      console.error('Error fetching most viewed articles:', error);
      setMostViewedArticles([]);
    }
  };

  // Initial load and on page/search/category change
  useEffect(() => {
    fetchCategories();
    fetchMostViewed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchArticles(currentPage, searchQuery, selectedCategory);
  }, [currentPage, searchQuery, selectedCategory]);

  // Update URL search params
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchQuery) params.set('busca', searchQuery);
    if (selectedCategory) params.set('categoria', selectedCategory);
    if (currentPage > 1) params.set('page', currentPage);
    setSearchParams(params);
  }, [searchQuery, selectedCategory, currentPage, setSearchParams]);

  // Handle search input
  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  // Handle category filter
  const handleCategoryClick = (categoryId) => {
    if (selectedCategory === categoryId) {
      setSelectedCategory('');
    } else {
      setSelectedCategory(categoryId);
    }
    setCurrentPage(1);
  };

  // Separate featured and regular articles
  const featuredArticles = articles.filter((a) => a.destaque);
  const regularArticles = articles.filter((a) => !a.destaque);

  // Count articles per category from all fetched data
  const categoryCounts = {};
  articles.forEach((a) => {
    const catId = a.categoria_id || a.blog_categorias?.id;
    if (catId) categoryCounts[catId] = (categoryCounts[catId] || 0) + 1;
  });

  // Skeleton loader
  const SkeletonCard = () => (
    <div className="bg-card rounded-2xl shadow-card overflow-hidden animate-pulse">
      <div className="h-44 bg-muted" />
      <div className="p-5">
        <div className="h-3 bg-muted rounded w-20 mb-2" />
        <div className="h-4 bg-muted rounded w-32 mb-2" />
        <div className="h-3 bg-muted rounded w-48 mb-3" />
        <div className="flex gap-2">
          <div className="h-3 bg-muted rounded w-16" />
          <div className="h-3 bg-muted rounded w-12" />
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col bg-muted/30">
      <Header />

      {/* Hero Section - clean, light background */}
      <section className="pt-24 pb-8 md:pt-28 md:pb-10 bg-muted/40 border-b border-border">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center gap-5">
            <img
              src="/images/brand/bruno-principal.png"
              alt="Bruno"
              className="w-16 h-16 md:w-20 md:h-20 rounded-full object-cover flex-shrink-0 border-2 border-primary/20"
            />
            <div>
              <h1 className="text-2xl md:text-3xl font-bold font-display text-secondary mb-1">Blog QuantoCustaObra</h1>
              <p className="text-muted-foreground text-base max-w-2xl">
                Dicas, notícias e insights sobre construção civil, orçamentos e tecnologia
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="max-w-6xl mx-auto px-4 py-8 pb-16 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content Area */}
          <div className="lg:col-span-2 space-y-6">
            {/* Search and Filter */}
            <div className="space-y-4">
              {/* Search Input */}
              <div className="relative">
                <input
                  type="text"
                  placeholder="Buscar artigos..."
                  value={searchQuery}
                  onChange={handleSearch}
                  className="w-full px-4 py-3 pl-10 rounded-lg border border-border focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
                <svg
                  className="absolute left-3 top-3.5 w-5 h-5 text-muted-foreground"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>

              {/* Category Filters */}
              <div className="space-y-2">
                <p className="text-sm font-semibold text-secondary">Categorias</p>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => handleCategoryClick('')}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      selectedCategory === ''
                        ? 'bg-primary text-white shadow-sm'
                        : 'bg-card border border-border text-foreground hover:border-primary/50'
                    }`}
                  >
                    Todos
                  </button>
                  {categories.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => handleCategoryClick(cat.id)}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                        selectedCategory === cat.id
                          ? 'bg-primary text-white shadow-sm'
                          : 'bg-card border border-border text-foreground hover:border-primary/50'
                      }`}
                    >
                      {cat.nome}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Featured Articles */}
            {featuredArticles.length > 0 && (
              <div className="space-y-4">
                <h2 className="text-lg font-bold text-secondary font-display">Destaques</h2>
                <div className="space-y-4">
                  {featuredArticles.map((artigo) => (
                    <ArticleCard key={artigo.id} artigo={artigo} featured={true} />
                  ))}
                </div>
              </div>
            )}

            {/* Regular Articles Grid */}
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[...Array(6)].map((_, i) => (
                  <SkeletonCard key={i} />
                ))}
              </div>
            ) : regularArticles.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {regularArticles.map((artigo) => (
                  <ArticleCard key={artigo.id} artigo={artigo} />
                ))}
              </div>
            ) : (
              <div className="bg-card rounded-2xl shadow-card p-8 text-center">
                <svg
                  className="w-12 h-12 mx-auto text-muted-foreground mb-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"
                  />
                </svg>
                <p className="text-muted-foreground text-sm">Nenhum artigo encontrado. Tente outro filtro ou busca.</p>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-8">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 rounded-lg border border-border text-foreground hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Anterior
                </button>

                {[...Array(totalPages)].map((_, i) => {
                  const page = i + 1;
                  // Show first, last, current, and adjacent pages
                  if (
                    page === 1 ||
                    page === totalPages ||
                    (page >= currentPage - 1 && page <= currentPage + 1)
                  ) {
                    return (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`px-3 py-2 rounded-lg font-medium transition-colors ${
                          currentPage === page
                            ? 'bg-primary text-white'
                            : 'border border-border text-foreground hover:bg-muted'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  }

                  // Show ellipsis for skipped pages
                  if (page === currentPage - 2 || page === currentPage + 2) {
                    return (
                      <span key={`ellipsis-${page}`} className="px-2 text-muted-foreground">
                        ...
                      </span>
                    );
                  }

                  return null;
                })}

                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 rounded-lg border border-border text-foreground hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Próxima
                </button>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            {/* Categories with count */}
            <div className="bg-card rounded-2xl shadow-card p-6">
              <h3 className="text-base font-bold text-secondary mb-4 font-display">Categorias</h3>
              <div className="space-y-1">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => handleCategoryClick(cat.id)}
                    className={`w-full text-left px-3 py-2.5 rounded-lg transition-colors flex items-center justify-between group ${
                      selectedCategory === cat.id ? 'bg-primary/10' : 'hover:bg-muted'
                    }`}
                  >
                    <span className={`text-sm font-medium ${
                      selectedCategory === cat.id ? 'text-primary' : 'text-foreground group-hover:text-secondary'
                    }`}>
                      {cat.nome}
                    </span>
                    <span className={`text-xs font-bold px-2.5 py-1 rounded-full transition-colors ${
                      selectedCategory === cat.id
                        ? 'bg-primary text-white'
                        : 'bg-primary/10 text-primary'
                    }`}>
                      {categoryCounts[cat.id] || 0}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Most Viewed Articles */}
            <div className="bg-card rounded-2xl shadow-card p-6">
              <h3 className="text-base font-bold text-secondary mb-4 font-display">Mais Lidos</h3>
              <div className="space-y-3">
                {mostViewedArticles.length > 0 ? (
                  mostViewedArticles.map((artigo, idx) => (
                    <Link
                      key={artigo.id}
                      to={`/blog/${artigo.slug}`}
                      className="group block no-underline"
                    >
                      <div className="flex gap-3">
                        <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                          <span className="text-sm font-bold text-primary">{idx + 1}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors line-clamp-2">
                            {artigo.titulo}
                          </h4>
                          <p className="text-xs text-muted-foreground mt-1">
                            {artigo.tempo_leitura || 5} min de leitura
                          </p>
                        </div>
                      </div>
                    </Link>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">Nenhum artigo disponível</p>
                )}
              </div>
            </div>

            {/* CTA Card */}
            <div className="bg-card rounded-2xl shadow-elevated p-6 border border-border">
              <div className="flex flex-col items-center text-center mb-4">
                <img
                  src="/images/brand/bruno-principal.png"
                  alt="Bruno"
                  className="w-20 h-20 mb-3 object-contain"
                />
              </div>
              <h3 className="text-lg font-bold mb-2 font-display text-secondary">Calcule seu Orçamento</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Envie sua planta e receba um orçamento completo em minutos.
              </p>
              <Link
                to="/orcamento/novo"
                className="w-full block text-center px-4 py-2.5 rounded-xl btn-cta text-sm no-underline"
              >
                Começar Agora
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default BlogList;
